/**
 * Syncs the free-shipping message with the gross cart total rendered by BSS.
 * Liquid's cart total remains a fallback until BSS injects its price spans.
 */
if (!window.Eurus.loadedScript.has('bss-free-shipping.js')) {
  window.Eurus.loadedScript.add('bss-free-shipping.js');

  (() => {
    'use strict';

    const OFFER_SELECTOR = '#CartDrawer [data-bss-freeshipping]';
    const TOTAL_SELECTOR = '#cart-total-price';
    const GROSS_SELECTOR = '.bss-b2b-price-has-vat';
    const watchedTotals = new WeakSet();
    const watchedFooters = new WeakSet();
    const fallbackTimers = new WeakSet();
    const initialGrossByTotal = new WeakMap();
    let settleTimer;

    const parsePriceCents = (text) => {
      const match = String(text || '').match(/-?\d[\d .,\u00A0]*\d|-?\d/);
      if (!match) return null;

      const raw = match[0].replace(/[\s\u00A0]/g, '');
      const decimalIndex = Math.max(raw.lastIndexOf(','), raw.lastIndexOf('.'));
      const normalized = decimalIndex < 0
        ? raw.replace(/[.,]/g, '')
        : `${raw.slice(0, decimalIndex).replace(/[.,]/g, '')}.${raw.slice(decimalIndex + 1)}`;
      const amount = Number.parseFloat(normalized);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    };

    const formatMoney = (cents, currency) => new Intl.NumberFormat(
      document.documentElement.lang || 'pl-PL',
      {
        style: 'currency',
        currency: currency || 'PLN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(cents / 100);

    const syncOffer = (offer, allowFallback = false) => {
      const thresholdCents = Number(offer.dataset.thresholdCents);
      const fallbackCents = Number(offer.dataset.shopifyTotalCents);
      const message = offer.querySelector('[data-bss-freeshipping-message]');
      if (!Number.isFinite(thresholdCents) || thresholdCents <= 0 || !message) return;

      const total = offer.closest('#CartDrawer')?.querySelector(TOTAL_SELECTOR);
      const bssGrossCents = parsePriceCents(total?.querySelector(GROSS_SELECTOR)?.textContent);
      const bssReady = total?.getAttribute('bss-b2b-cart-price-active') === 'true'
        && total.style.visibility === 'visible';
      if ((!bssReady || bssGrossCents === null) && !allowFallback) {
        if (!fallbackTimers.has(offer)) {
          fallbackTimers.add(offer);
          // The BSS settle window has already elapsed. If no BSS total exists
          // now, reveal the Shopify fallback instead of leaving a blank gap.
          setTimeout(() => syncOffer(offer, true), 500);
        }
        return;
      }

      const totalCents = bssReady && bssGrossCents !== null
        ? bssGrossCents
        : fallbackCents;
      if (!Number.isFinite(totalCents)) return;

      const remainingCents = Math.max(0, thresholdCents - totalCents);
      const html = remainingCents === 0
        ? offer.dataset.successMessage
        : offer.dataset.amountTemplate.replace(
          '__BSS_AMOUNT__',
          formatMoney(remainingCents, offer.dataset.currency),
        );

      // The total can mutate more than once while BSS is rendering. Do not
      // rewrite unchanged markup.
      if (message.innerHTML !== html) message.innerHTML = html;

      const bar = offer.querySelector('[data-bss-freeshipping-bar]');
      if (bar) {
        const eligible = remainingCents === 0;
        bar.classList.toggle('free-offer-bar', !eligible);
        bar.classList.toggle('free-offer-bar-success', eligible);
        bar.classList.add('bss-freeshipping-initialized');
        bar.style.setProperty(
          '--bss-freeshipping-progress',
          `${Math.min(100, (totalCents / thresholdCents) * 100)}%`,
        );
      }

      offer.dataset.bssFreeshippingState = 'ready';
    };

    const syncAllOffers = () => {
      document.querySelectorAll(OFFER_SELECTOR).forEach(syncOffer);
    };

    const markAllOffersLoading = () => {
      document.querySelectorAll(OFFER_SELECTOR).forEach((offer) => {
        offer.dataset.bssFreeshippingState = 'loading';
      });
    };

    // If BSS sends only one value, reveal it after a short fallback wait.
    // Normally the first value is stale and a changed second value is used
    // immediately by handleTotalMutation below.
    const scheduleSync = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        syncAllOffers();
      }, 1500);
    };

    const handleTotalMutation = (total) => {
      markAllOffersLoading();

      const grossCents = parsePriceCents(total.querySelector(GROSS_SELECTOR)?.textContent);
      if (grossCents === null) {
        scheduleSync();
        return;
      }

      const initialGross = initialGrossByTotal.get(total);
      if (initialGross === undefined) {
        initialGrossByTotal.set(total, grossCents);
        scheduleSync();
        return;
      }

      if (grossCents !== initialGross) {
        clearTimeout(settleTimer);
        syncAllOffers();
      }
    };

    const watchTotal = (total) => {
      if (watchedTotals.has(total)) return;
      watchedTotals.add(total);
      const initialGross = parsePriceCents(total.querySelector(GROSS_SELECTOR)?.textContent);
      if (initialGross !== null) initialGrossByTotal.set(total, initialGross);
      new MutationObserver(() => handleTotalMutation(total)).observe(total, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    };

    const scan = () => {
      document.querySelectorAll('#CartDrawer .cart-drawer-footer').forEach(watchFooter);
      document.querySelectorAll(`#CartDrawer ${TOTAL_SELECTOR}`).forEach(watchTotal);
    };

    // Theme AJAX updates replace the footer contents. This observer is scoped
    // to that footer, never to the full drawer, and therefore cannot react to
    // the message update in the cart-items area.
    const watchFooter = (footer) => {
      if (watchedFooters.has(footer)) return;
      watchedFooters.add(footer);
      new MutationObserver(() => {
        markAllOffersLoading();
        scan();
        scheduleSync();
      }).observe(footer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    };

    const init = () => {
      document.querySelectorAll('#CartDrawer .cart-drawer-footer').forEach(watchFooter);
      markAllOffersLoading();
      scan();
      scheduleSync();
      document.addEventListener('eurus:section:rendered', () => {
        markAllOffersLoading();
        scan();
        scheduleSync();
      });
      document.addEventListener('eurus:cart:items-changed', () => {
        markAllOffersLoading();
        scan();
        scheduleSync();
      });
      document.addEventListener('eurus:cart:discount-code:change', () => {
        markAllOffersLoading();
        scan();
        scheduleSync();
      });
    };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  })();
}
